import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import playTokens from '@amzn/meridian-tokens/base/icon/play';

import HtmlToMeridian from './HtmlToMeridian';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import useCustomTracking from '../../Util/useCustomTracking';
import useInspireOptions from '../../hooks/useInspireOptions';
import useResourceHubAnnouncements, {
  ParsedResourceHubAnnouncement,
} from '../../api/ResourceHub/useResourceHubAnnouncements';

const ResourceHubAnnouncements: React.FC = () => {
  const { loading, error, data } = useResourceHubAnnouncements();

  if (loading) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader type='circular' size='large' data-testid='loading-spinner' />
      </div>
    );
  }

  if (error) {
    return (
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_refresh_page'
          defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
        />
      </Alert>
    );
  }

  return (
    <>
      {data?.map(announcement => {
        return (
          <React.Fragment key={announcement.guid}>
            <ResourceHubAnnouncement {...announcement} />
            <Divider spacingBefore='400' spacingAfter='400' />
          </React.Fragment>
        );
      })}
    </>
  );
};

export const ResourceHubAnnouncement: React.FC<
  ParsedResourceHubAnnouncement
> = ({ content, metadata, guid }) => {
  const intl = useIntl();
  const { isTm, requesterUserID, requesterUsername } = useInspireOptions();

  const { launchDate, videoUrl } = metadata;

  const [ClickstreamTag, emitClickstreamClick] = useCustomTracking(
    `<resource-hub>-video-click-for-${guid}`,
    '<resource-hub>-video-click',
    {
      'data-csa-c-type': 'button',
      'data-csa-c-tag1': '<resource-hub>',
      'data-csa-c-tag2': 'Announcement',
      'data-csa-c-tag3': 'Video Click',
      'data-csa-c-profile-id': requesterUserID,
      'data-csa-c-username': requesterUsername,
      'data-csa-c-is-tm': isTm,
    }
  );

  const handleVideoUrlClick = (videoUrl: string) => {
    emitClickstreamClick();
    window.open(videoUrl, '_blank');
  };

  return (
    <Column>
      <Heading level={3} type='h400'>
        {intl.formatDate(launchDate?.toDate(), {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })}
      </Heading>
      {videoUrl && (
        <Box spacingInset='none none 400 none'>
          <Button
            type='tertiary'
            href={videoUrl}
            target='_blank'
            onClick={() => handleVideoUrlClick(videoUrl)}
          >
            <Icon tokens={playTokens} />{' '}
            <FormattedMessage
              id='cs_perfportal_play_video'
              defaultMessage='Play Video'
            />
          </Button>
          <ClickstreamTag />
        </Box>
      )}
      <HtmlToMeridian html={content} />
    </Column>
  );
};

export default ResourceHubAnnouncements;
